/**
 * Where a story preview gets each scene (spec 009): the open tab's in-memory
 * state, else its draft-cache entry (spec 004), else `GET /scenes/:id`. So
 * unsaved edits show in the pane, the runner and the pop-out.
 */

import type { StoryApiResponse } from '../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import { apiClient } from '../api/client';
import { previewSceneInput } from '../stores/sceneStore';
import { sceneSpriteMediaIds, useMediaSheetStore } from '../stores/mediaSheetStore';

/**
 * The scene as the preview plays it; `null` when it cannot be loaded. The
 * spritesheet cuts of its pictures are loaded with it (spec 033), so
 * `buildPreviewManifest` finds them.
 */
export async function loadPreviewScene(sceneId: number): Promise<PlayableSceneInput | null> {
  const scene = await loadSceneInput(sceneId);
  if (scene) await useMediaSheetStore.getState().loadSheets(sceneSpriteMediaIds(scene as never));
  return scene;
}

async function loadSceneInput(sceneId: number): Promise<PlayableSceneInput | null> {
  const local = previewSceneInput(sceneId);
  if (local) return local as PlayableSceneInput;
  try {
    const res = await apiClient.getScene(sceneId);
    return res.success && res.data ? (res.data as unknown as PlayableSceneInput) : null;
  } catch {
    return null;
  }
}

/** Every scene on the story's graph, keyed by `StoryScene.id`; scenes that fail to load are left out. */
export async function loadStoryPreviewScenes(
  story: Pick<StoryApiResponse, 'graph' | 'storyScenes'>,
): Promise<Record<number, PlayableSceneInput>> {
  const onGraph = new Set(story.graph.nodes.filter((n) => n.type !== 'end').map((n) => n.storySceneId));
  const rows = story.storyScenes.filter((ss) => onGraph.has(ss.id));
  const loaded = await Promise.all(rows.map((ss) => loadPreviewScene(ss.sceneId)));
  const out: Record<number, PlayableSceneInput> = {};
  rows.forEach((ss, i) => {
    const scene = loaded[i];
    if (scene) out[ss.id] = { ...scene, name: scene.name || ss.sceneName || `Scene #${ss.sceneId}` };
  });
  return out;
}

/** The BroadcastChannel the story editor and the pop-out preview share. */
export const PREVIEW_CHANNEL = 'sm-story-preview';

/** Messages on `PREVIEW_CHANNEL`. */
export type PreviewMessage =
  | { type: 'orientation-change'; orientation: 'desktop' | 'portrait' | 'landscape' }
  | { type: 'scene-update'; sceneId: number; scene: PlayableSceneInput }
  | { type: 'popout-ready' }
  | { type: 'popout-closed' }
  | { type: 'close' };
